/*
 * Preventative Maintenance Table
 */

import * as React from 'react';
import { useTable, useSortBy, Column, useFlexLayout } from 'react-table';

import { IjobHour, Itile } from '../../models';
import { TFunction } from 'react-i18next';
import moment from 'moment';

import { TableUtil } from '../common/TableUtil';
import { constants } from '../../constants/constants';
import {
    unstageJobHour as unstageJobHourAction,
    setSelectedJobHourID as setSelectedJobHourIDAction
} from '../../actions/manageJobActions';
import { HoursRowActions } from './HoursRowActions';

const buildColumns = (
    t: TFunction,
    onEdit: (id: string) => void,
    onDelete: (jobHour: IjobHour) => void,
    disabled: boolean,
    stagedIds: string[]
): Column<IjobHour>[] =>
    TableUtil.translateHeaders(
        [
            {
                Header: <span>{t('jobHoursForm.laborRateHeader')}</span>,
                accessor: ({ number, description }: IjobHour) => {
                    return `${number} - ${description}`;
                },
                width: 260,
                id: 'laborRate'
            },
            {
                Header: <span>{t('jobHoursForm.engineerVanIdHeader')}</span>,
                accessor: 'engineerVanID',
                width: 120
            },
            {
                Header: <span>{t('jobHoursForm.hoursHeader')}</span>,
                accessor: 'quantity',
                width: 80
            },
            {
                Header: <span>{t('jobHoursForm.dateHeader')}</span>,
                id: 'date',
                width: 100,
                accessor: ({ date }: IjobHour) => {
                    return date
                        ? moment
                              .utc(date)
                              .local(true)
                              .format(constants.momentDisplayFormat)
                        : 'n/a';
                }
            },
            {
                Header: <span />,
                id: 'actions',
                width: 60,
                Cell: ({ row }: any) => {
                    const jobHour: IjobHour = row.original;
                    // Only rows staged this session (not yet saved to the API) are editable/deletable
                    if (disabled || !stagedIds.includes(jobHour.id)) {
                        return null;
                    }
                    return <HoursRowActions jobHour={jobHour} t={t} onEdit={onEdit} onDelete={onDelete} />;
                }
            }
        ],
        t
    ) as Column<IjobHour>[];

interface Iprops {
    tableData: IjobHour[];
    t: TFunction;
    loading: boolean;
    currentTile: Itile;
    disabled?: boolean;
    selectedJobHourID: string;
    setSelectedJobHourID: typeof setSelectedJobHourIDAction;
    unstageJobHour: typeof unstageJobHourAction;
    stagedIds: string[];
}

export const AddHoursTable: React.FC<Iprops> = props => {
    const { tableData, t, currentTile, disabled, selectedJobHourID, setSelectedJobHourID, unstageJobHour, stagedIds } = props;

    const onEdit = React.useCallback((id: string) => setSelectedJobHourID(id), [setSelectedJobHourID]);
    const onDelete = React.useCallback(
        (jobHour: IjobHour) => {
            // Staged rows were never persisted, so removing is a local unstage (no API call)
            unstageJobHour(jobHour.id);
            // If the row being edited is deleted, reset the form back to add-mode
            if (jobHour.id === selectedJobHourID) {
                setSelectedJobHourID('');
            }
        },
        [unstageJobHour, setSelectedJobHourID, selectedJobHourID]
    );

    const columns = React.useMemo(() => buildColumns(t, onEdit, onDelete, disabled === true, stagedIds), [
        t,
        onEdit,
        onDelete,
        disabled,
        stagedIds
    ]);
    const data = React.useMemo(() => tableData, [tableData]);

    /*
     * MMG-4435: no pagination at all. Rows are the job's saved hours plus any staged in this modal -
     * a few dozen at most - so every row renders and the table scrolls inside the modal.
     */
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
        {
            columns: columns as Column<IjobHour>[],
            data
        },
        useSortBy,
        useFlexLayout
    );

    const tableClassName = `ReactTable beacon-table add-hours-table flex-layout -highlight ${currentTile.color}`;

    return (
        <div>
            <div {...getTableProps()} className={tableClassName}>
                <div className="rt-thead -header">
                    {headerGroups.map((headerGroup: any) => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map((column: any) => (
                                <div {...column.getHeaderProps()} key={column.getHeaderProps().key} className="rt-th">
                                    <div>{column.render('Header')}</div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {rows.map((row: any, i: number) => {
                        prepareRow(row);
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                className={`rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`}
                            >
                                {row.cells.map((cell: any) => {
                                    return (
                                        <div {...cell.getCellProps()} key={cell.getCellProps().key} className="rt-td">
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
